import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { EVENT, PACKAGE_LABELS, RATECARD, isEarlyBird, money } from "@/lib/site";
import { useSeo } from "@/lib/seo";
import { ExitPopup } from "@/components/site/Growth";

const RATES = {
  delegate: { early: [7500, 600], standard: [10000, 1100] },
  stall6: [80000, 2000],
  stall9: [125000, 2500],
  corporate: [15000, 1500],
  startup: [11000, 2000],
};

const COUNTRIES = ["AE AR AT AU BD BE BH BR CA CH CL CN CO CZ DE DK EG ES FI FR GB GH GR HK HU ID IE IL IN IT JP KE KR KW LK MA MX MY NG NL NO NP NZ OM PE PH PK PL PT QA RO RS RU SA SE SG TH TR TW TZ UA US VN ZA"];
const COUNTRY_NAMES = (() => {
  try {
    const display = new Intl.DisplayNames(["en"], { type: "region" });
    return COUNTRIES[0].split(" ").map((code) => display.of(code)).sort();
  } catch {
    return [];
  }
})();

const emptyDelegate = () => ({ firstName: "", lastName: "", email: "", phone: "", designation: "" });

export default function RegisterPage() {
  useSeo({
    title: "Register | OFIC 2026 — 11 December, Ginger Hotel Mumbai",
    description: "Register for the Organic Food India Conclave 2026. Delegate passes, exhibition stalls and awards packages. 11 December 2026, Ginger Hotel, Mumbai.",
  });
  const [params] = useSearchParams();
  const [config, setConfig] = useState({ payments: false, gstPercent: null });
  const [pkg, setPkg] = useState(() => (PACKAGE_LABELS[params.get("package")] ? params.get("package") : "delegate"));
  const [currency, setCurrency] = useState(() => (params.get("currency") === "USD" ? "USD" : "INR"));
  const [delegates, setDelegates] = useState([emptyDelegate()]);
  const [company, setCompany] = useState("");
  const [country, setCountry] = useState("India");
  const [city, setCity] = useState("");
  const [gstin, setGstin] = useState("");
  const [hotel, setHotel] = useState("none");
  const [hotelInfo, setHotelInfo] = useState({ checkIn: "", checkOut: "", rooms: 1, notes: "" });
  const [paymentMethod, setPaymentMethod] = useState("bank");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState({ text: "", error: false });
  const [receipt, setReceipt] = useState(null);
  const lastAbandoned = useRef(0);
  const submitted = useRef(false);

  const early = isEarlyBird();
  const stall = pkg.startsWith("stall");
  const ratePair = pkg === "delegate" ? (early ? RATES.delegate.early : RATES.delegate.standard) : RATES[pkg];
  const unit = ratePair[currency === "INR" ? 0 : 1];
  const quantity = stall ? 1 : delegates.length;
  const base = unit * quantity;
  const gstKnown = currency === "USD" || config.gstPercent !== null;
  const tax = currency === "INR" && config.gstPercent !== null ? Math.round(base * config.gstPercent) / 100 : 0;
  const total = base + tax;

  useEffect(() => {
    api.get("/config").then(({ data }) => {
      setConfig(data);
      if (data.payments) setPaymentMethod("online");
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (currency === "INR") setCountry("India");
    else if (country === "India") setCountry("");
  }, [currency]); // eslint-disable-line react-hooks/exhaustive-deps

  const setDelegate = (index, field, value) => {
    setDelegates((prev) => prev.map((d, i) => (i === index ? { ...d, [field]: value } : d)));
  };
  const addDelegate = () => {
    if (delegates.length >= 10 || (stall && delegates.length >= 1)) return;
    setDelegates((prev) => [...prev, emptyDelegate()]);
  };
  const removeDelegate = (index) => {
    setDelegates((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };
  useEffect(() => {
    if (stall && delegates.length > 1) setDelegates((prev) => prev.slice(0, 1));
  }, [stall]); // eslint-disable-line react-hooks/exhaustive-deps

  // Abandoned-cart tracking: capture once the primary email is valid and progress stalls.
  useEffect(() => {
    if (submitted.current) return;
    const email = delegates[0]?.email?.trim() || "";
    const valid = email.includes("@") && email.includes(".") && email.length > 4;
    if (!valid) return undefined;
    const timer = setTimeout(() => {
      if (Date.now() - lastAbandoned.current < 60000) return;
      lastAbandoned.current = Date.now();
      api.post("/abandoned", {
        email,
        name: `${delegates[0].firstName} ${delegates[0].lastName}`.trim(),
        package: pkg,
        package_name: PACKAGE_LABELS[pkg],
        currency,
        participants: quantity,
        total_display: money(total, currency) + (currency === "INR" && !gstKnown ? " + GST" : ""),
        step: "form",
      }).catch(() => {});
    }, 6000);
    return () => clearTimeout(timer);
  }, [delegates, pkg, currency, quantity, total, gstKnown]);

  const payload = useMemo(
    () => ({
      package: pkg,
      currency,
      company,
      country,
      city,
      gstin: currency === "INR" ? gstin : "",
      delegates: delegates.map((d) => ({ ...d, firstName: d.firstName.trim(), lastName: d.lastName.trim(), email: d.email.trim(), phone: d.phone.trim(), designation: d.designation.trim() })),
      name: `${delegates[0].firstName} ${delegates[0].lastName}`.trim(),
      email: delegates[0].email.trim(),
      phone: delegates[0].phone.trim(),
      designation: delegates[0].designation.trim(),
      paymentMethod,
      accommodation: hotel === "enquiry" ? { requested: true, ...hotelInfo } : { requested: false },
      consent: consent ? "on" : "",
    }),
    [pkg, currency, company, country, city, gstin, delegates, paymentMethod, hotel, hotelInfo, consent],
  );

  const showStatus = (text, error = false) => {
    setStatus({ text, error });
    document.querySelector('[data-testid="booking-status"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const loadCheckout = async () => {
    if (window.Razorpay) return;
    await new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = resolve;
      script.onerror = () => reject(new Error("Unable to load secure checkout. Try again or contact the organiser."));
      document.head.appendChild(script);
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    const emails = delegates.map((d) => d.email.toLowerCase().trim());
    if (new Set(emails).size !== emails.length) {
      showStatus("Use a different email address for each delegate so everyone receives their confirmation.", true);
      return;
    }
    if (hotel === "enquiry" && hotelInfo.checkIn && hotelInfo.checkOut && hotelInfo.checkOut <= hotelInfo.checkIn) {
      showStatus("Check-out must be after check-in.", true);
      return;
    }
    if (paymentMethod === "online" && !config.payments) {
      showStatus("Online payments are not available yet. Please choose bank transfer.", true);
      return;
    }
    setBusy(true);
    showStatus("Saving your registration…");
    try {
      const { data } = await api.post("/register", payload);
      submitted.current = true;
      if (!data.order) {
        setReceipt({ id: data.id, paid: false });
        return;
      }
      showStatus(`Order ready. Complete payment in the Razorpay window. Reference: ${data.id}`);
      await loadCheckout();
      let handled = false;
      const checkout = new window.Razorpay({
        key: data.key,
        order_id: data.order.id,
        amount: data.order.amount,
        currency: data.order.currency,
        name: "OFIC 2026",
        description: `${PACKAGE_LABELS[pkg]} · ${quantity} participant${quantity > 1 ? "s" : ""}`,
        prefill: data.prefill,
        theme: { color: "#123d30" },
        handler: async (payment) => {
          handled = true;
          showStatus("Verifying payment. Please keep this page open…");
          try {
            const verify = await api.post("/verify", payment);
            if (verify.data.paid) setReceipt({ id: data.id, paid: true });
            else showStatus(`Payment is awaiting capture. Reference: ${data.id}. You will receive email confirmation once capture is verified. Please do not pay again.`);
          } catch (err) {
            showStatus(`Payment verification is pending. Keep reference ${data.id} and your Razorpay payment ID. Contact ${EVENT.email} before paying again.`, true);
          }
        },
        modal: {
          ondismiss: () => {
            if (!handled) {
              showStatus(`Checkout closed. Reference: ${data.id}. Payment is not confirmed. If money was debited, contact the organiser before trying again.`, true);
              setBusy(false);
            }
          },
        },
      });
      checkout.on("payment.failed", () => showStatus(`Payment was unsuccessful. You can retry in the Razorpay window. Reference: ${data.id}. If money was debited, contact Tefla's before paying again.`, true));
      checkout.open();
    } catch (err) {
      setBusy(false);
      showStatus(formatApiErrorDetail(err.response?.data?.detail) || "Unable to connect. Please contact " + EVENT.email + ".", true);
    }
  };

  const summaryLine = (label, value) => (
    <div key={label} className="flex items-baseline justify-between gap-3 py-2 text-[13px]">
      <span className="text-[#5c6b5a]">{label}</span>
      <strong>{value}</strong>
    </div>
  );

  return (
    <div data-testid="register-page" className="min-h-screen bg-[#eaeadd] pb-20">
      <ExitPopup />
      <header className="border-b bg-[#f3f3e9]" style={{ borderColor: "var(--line)" }}>
        <div className="wrap flex h-[68px] items-center justify-between">
          <Link to="/" aria-label="OFIC 2026 home" data-testid="register-logo-link">
            <img src="/assets/ofic-mark.png" alt="OFIC 2026" className="h-10 w-auto" />
          </Link>
          <Link to="/" data-testid="register-back-home" className="text-[13px] font-bold no-underline opacity-70 hover:opacity-100">← Back to the conclave</Link>
          <a href={`mailto:${EVENT.email}`} data-testid="register-help-link" className="hidden text-[13px] font-bold no-underline sm:block">Need help? Contact Tefla's ↗</a>
        </div>
      </header>

      <main className="wrap pt-12">
        <section data-testid="register-heading" className="max-w-2xl">
          <p className="eyebrow text-[#5c6b5a]">Register / Organic Food India Conclave 2026</p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="mt-3 font-display text-4xl leading-tight sm:text-6xl">
            Your place in a
            <br />
            <em className="italic">greener future.</em>
          </motion.h1>
          <p className="mt-4 text-[15px] leading-relaxed text-[#5c6b5a]">
            11 December, 2026 · Ginger Hotel, Mumbai
            <br />
            Choose your participation, add your details and review your booking.
          </p>
        </section>

        <div className="mt-12 grid gap-8 lg:grid-cols-[1fr_380px]">
          <form onSubmit={submit} data-testid="booking-form" noValidate={false}>
            <section className="grain-card rounded-2xl p-6 sm:p-8" data-testid="form-section-1">
              <h2 className="flex items-center gap-3 font-display text-xl sm:text-2xl">
                <span className="grid h-8 w-8 place-items-center rounded-full text-sm" style={{ background: "var(--forest)", color: "var(--lime)" }}>1</span>
                Registration type & company
              </h2>
              <div className="mt-6 grid gap-5">
                <div>
                  <label className="field-label" htmlFor="booking-package">Participation package *</label>
                  <select id="booking-package" data-testid="booking-package" className="field-input" value={pkg} onChange={(e) => setPkg(e.target.value)}>
                    <option value="delegate">Delegate participation · Package 1</option>
                    <option value="stall6">6 sq. m exhibition stall</option>
                    <option value="stall9">9 sq. m exhibition stall</option>
                    <option value="corporate">Awards & gala · Corporates / Entrepreneurs</option>
                    <option value="startup">Awards & gala · Startups / NGOs</option>
                  </select>
                  <p className="mt-2 text-[12px] leading-relaxed text-[#5c6b5a]" data-testid="package-description">
                    {stall
                      ? "Stall availability and allocation must be confirmed by the organiser. Contact details are for the primary exhibitor contact."
                      : pkg === "corporate" || pkg === "startup"
                      ? "Package 2 covers only the award nomination entry fee and gala dinner, 11 December 2026, 7:00 pm onwards. Nomination does not guarantee an award."
                      : "Participation · Package 1. Indian and international fees follow the official OFIC 2026 rate card."}
                  </p>
                  {stall && (
                    <a href="/assets/layout.pdf" target="_blank" rel="noreferrer" data-testid="register-layout-link" className="text-[12px] font-bold underline">
                      View the expo floor plan (zones A & B · 27 stalls) ↗
                    </a>
                  )}
                </div>
                <span className="field-label">Registering as *</span>
                <div className="grid gap-4 sm:grid-cols-2">
                  {[["INR", "Indian participant", "Domestic billing · INR"], ["USD", "International participant", "International billing · USD"]].map(([value, title, desc]) => (
                    <label key={value} className={`choice-card ${currency === value ? "selected" : ""}`} data-testid={`currency-choice-${value.toLowerCase()}`}>
                      <input type="radio" name="currency" value={value} checked={currency === value} onChange={() => setCurrency(value)} />
                      <span className="text-sm font-extrabold">{title}</span>
                      <span className="text-[12px] text-[#5c6b5a]">{desc}</span>
                      <strong className="mt-1 text-lg" data-testid={`currency-price-${value.toLowerCase()}`}>
                        {value === "INR" ? `${money(ratePair[0], "INR")} + GST` : `${money(ratePair[1], "USD")}`}
                      </strong>
                      <span className="text-[11px] font-bold" style={{ color: currency === value ? "var(--forest)" : "var(--moss)" }}>{currency === value ? "✓ Selected" : "Tap to select"}</span>
                    </label>
                  ))}
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="sm:col-span-2">
                    <span className="field-label">Company / organisation *</span>
                    <input data-testid="company-input" className="field-input" required maxLength={160} autoComplete="organization" value={company} onChange={(e) => setCompany(e.target.value)} />
                  </label>
                  <label>
                    <span className="field-label">Country *</span>
                    <input data-testid="country-input" className="field-input" required maxLength={80} autoComplete="country-name" list="countries" value={country} onChange={(e) => setCountry(e.target.value)} />
                    <datalist id="countries">
                      {COUNTRY_NAMES.map((name) => (
                        <option key={name} value={name} />
                      ))}
                    </datalist>
                  </label>
                  <label>
                    <span className="field-label">City *</span>
                    <input data-testid="city-input" className="field-input" required maxLength={100} autoComplete="address-level2" value={city} onChange={(e) => setCity(e.target.value)} />
                  </label>
                  {currency === "INR" && (
                    <label className="sm:col-span-2">
                      <span className="field-label">GSTIN (optional)</span>
                      <input data-testid="gstin-input" className="field-input" maxLength={15} pattern="[0-9A-Za-z]{15}" placeholder="For GST billing, if applicable" value={gstin} onChange={(e) => setGstin(e.target.value)} />
                    </label>
                  )}
                </div>
              </div>
            </section>

            <section className="grain-card mt-6 rounded-2xl p-6 sm:p-8" data-testid="form-section-2">
              <h2 className="flex items-center gap-3 font-display text-xl sm:text-2xl">
                <span className="grid h-8 w-8 place-items-center rounded-full text-sm" style={{ background: "var(--forest)", color: "var(--lime)" }}>2</span>
                {stall ? "Primary contact details" : "Delegate details"}
              </h2>
              <p className="mt-3 text-[13px] leading-relaxed text-[#5c6b5a]">
                {stall ? "One primary contact for this exhibition stall enquiry. Included delegate access must be confirmed by Tefla's." : "Add up to 10 delegates in one booking. The first delegate is the primary contact."}
              </p>
              <div className="mt-6 grid gap-5">
                {delegates.map((delegate, index) => (
                  <div key={index} className="rounded-xl p-5" style={{ background: "#fbfbf4", border: "1px solid var(--line)" }} data-testid={`delegate-card-${index}`}>
                    <div className="flex items-center justify-between">
                      <span className="eyebrow text-[10px] text-[#5c6b5a]">
                        Delegate #{index + 1}
                        {index === 0 ? " · Primary contact" : ""}
                      </span>
                      {index > 0 && (
                        <button type="button" data-testid={`remove-delegate-${index}`} onClick={() => removeDelegate(index)} className="text-[12px] font-bold text-red-900 underline">
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      <label>
                        <span className="field-label">First name *</span>
                        <input data-testid={`delegate-first-name-${index}`} className="field-input" required maxLength={70} autoComplete="given-name" value={delegate.firstName} onChange={(e) => setDelegate(index, "firstName", e.target.value)} />
                      </label>
                      <label>
                        <span className="field-label">Last name *</span>
                        <input data-testid={`delegate-last-name-${index}`} className="field-input" required maxLength={70} autoComplete="family-name" value={delegate.lastName} onChange={(e) => setDelegate(index, "lastName", e.target.value)} />
                      </label>
                      <label className="sm:col-span-2">
                        <span className="field-label">Email *</span>
                        <input data-testid={`delegate-email-${index}`} className="field-input" type="email" required maxLength={180} autoComplete="email" value={delegate.email} onChange={(e) => setDelegate(index, "email", e.target.value)} />
                      </label>
                      <label>
                        <span className="field-label">Phone with country code *</span>
                        <input data-testid={`delegate-phone-${index}`} className="field-input" type="tel" required pattern="[+0-9 ()-]{7,25}" placeholder="+91 98765 43210" maxLength={25} autoComplete="tel" value={delegate.phone} onChange={(e) => setDelegate(index, "phone", e.target.value)} />
                      </label>
                      <label>
                        <span className="field-label">Designation *</span>
                        <input data-testid={`delegate-designation-${index}`} className="field-input" required maxLength={120} value={delegate.designation} onChange={(e) => setDelegate(index, "designation", e.target.value)} />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
              {!stall && delegates.length < 10 && (
                <button type="button" data-testid="add-delegate-btn" onClick={addDelegate} className="ofic-btn ofic-btn-ghost mt-5">
                  ＋ Add another delegate
                </button>
              )}
            </section>

            <section className="grain-card mt-6 rounded-2xl p-6 sm:p-8" data-testid="form-section-3">
              <h2 className="flex items-center gap-3 font-display text-xl sm:text-2xl">
                <span className="grid h-8 w-8 place-items-center rounded-full text-sm" style={{ background: "var(--forest)", color: "var(--lime)" }}>3</span>
                Accommodation
              </h2>
              <p className="mt-3 text-[13px] leading-relaxed text-[#5c6b5a]">Optional assistance with your stay. Hotel rates and room availability must be confirmed separately with Tefla's.</p>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                {[["none", "No assistance needed", "I'll arrange my own stay"], ["enquiry", "Enquire about accommodation", "Ask the organiser to contact me · no room charge added"]].map(([value, title, desc]) => (
                  <label key={value} className={`choice-card ${hotel === value ? "selected" : ""}`} data-testid={`hotel-choice-${value}`}>
                    <input type="radio" name="hotel" value={value} checked={hotel === value} onChange={() => setHotel(value)} />
                    <span className="text-sm font-extrabold">{title}</span>
                    <span className="text-[12px] text-[#5c6b5a]">{desc}</span>
                  </label>
                ))}
              </div>
              {hotel === "enquiry" && (
                <div className="mt-5 grid gap-4 sm:grid-cols-2" data-testid="hotel-fields">
                  <label>
                    <span className="field-label">Check-in date</span>
                    <input data-testid="hotel-checkin" type="date" className="field-input" min="2026-12-01" max="2026-12-31" value={hotelInfo.checkIn} onChange={(e) => setHotelInfo({ ...hotelInfo, checkIn: e.target.value })} />
                  </label>
                  <label>
                    <span className="field-label">Check-out date</span>
                    <input data-testid="hotel-checkout" type="date" className="field-input" min="2026-12-01" max="2027-01-15" value={hotelInfo.checkOut} onChange={(e) => setHotelInfo({ ...hotelInfo, checkOut: e.target.value })} />
                  </label>
                  <label>
                    <span className="field-label">Rooms requested</span>
                    <input data-testid="hotel-rooms" type="number" min="1" max="10" className="field-input" value={hotelInfo.rooms} onChange={(e) => setHotelInfo({ ...hotelInfo, rooms: Number(e.target.value) || 1 })} />
                  </label>
                  <label>
                    <span className="field-label">Notes</span>
                    <input data-testid="hotel-notes" className="field-input" maxLength={300} placeholder="Your accommodation preferences" value={hotelInfo.notes} onChange={(e) => setHotelInfo({ ...hotelInfo, notes: e.target.value })} />
                  </label>
                </div>
              )}
            </section>

            <section className="grain-card mt-6 rounded-2xl p-6 sm:p-8" data-testid="form-section-4">
              <h2 className="flex items-center gap-3 font-display text-xl sm:text-2xl">
                <span className="grid h-8 w-8 place-items-center rounded-full text-sm" style={{ background: "var(--forest)", color: "var(--lime)" }}>4</span>
                Review & payment
              </h2>
              <div className="mt-5 border-y py-2" style={{ borderColor: "var(--line)" }} data-testid="review-lines">
                {summaryLine(`${PACKAGE_LABELS[pkg]} × ${quantity}`, money(base, currency))}
                {currency === "INR" && summaryLine(config.gstPercent !== null ? `GST · ${config.gstPercent}%` : "GST", gstKnown ? money(tax, currency) : "To be confirmed")}
                <div className="flex items-baseline justify-between gap-3 border-t py-3" style={{ borderColor: "var(--line)" }}>
                  <span className="text-sm font-extrabold">{gstKnown ? "Total payable" : "Subtotal · GST additional"}</span>
                  <strong className="font-display text-2xl" data-testid="review-total">{money(total, currency)}</strong>
                </div>
              </div>
              <span className="field-label mt-6">Payment method *</span>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className={`choice-card ${paymentMethod === "online" ? "selected" : ""} ${!config.payments ? "opacity-50" : ""}`} data-testid="payment-choice-online">
                  <input type="radio" name="paymentMethod" value="online" checked={paymentMethod === "online"} disabled={!config.payments} onChange={() => setPaymentMethod("online")} />
                  <span className="text-sm font-extrabold">Pay online</span>
                  <span className="text-[12px] text-[#5c6b5a]">{config.payments ? "Razorpay secure checkout · receipt after verified capture" : "Awaiting activation — bank transfer available"}</span>
                </label>
                <label className={`choice-card ${paymentMethod === "bank" ? "selected" : ""}`} data-testid="payment-choice-bank">
                  <input type="radio" name="paymentMethod" value="bank" checked={paymentMethod === "bank"} onChange={() => setPaymentMethod("bank")} />
                  <span className="text-sm font-extrabold">Bank transfer</span>
                  <span className="text-[12px] text-[#5c6b5a]">Submit your details and contact Tefla's for payment arrangements</span>
                </label>
              </div>
              {paymentMethod === "bank" && (
                <div className="mt-5 rounded-xl p-5" style={{ background: "#fffef2", border: "1px solid var(--line)" }} data-testid="bank-note">
                  <strong className="text-sm">Bank transfer bookings require organiser confirmation.</strong>
                  <p className="mt-2 text-[13px] leading-relaxed text-[#3d5247]">
                    Submit this form to receive a registration reference. The official rate card contains Indian NEFT and international transfer instructions. Confirm the payable total and availability with Tefla's before sending funds.
                  </p>
                  <a href={RATECARD} target="_blank" rel="noreferrer" className="text-link mt-3 !text-[12px]">View bank details in the rate card ↗</a>
                </div>
              )}
              <label className="mt-6 flex items-start gap-3 text-[13px] leading-relaxed" data-testid="consent-row">
                <input data-testid="consent-checkbox" type="checkbox" required checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 h-4 w-4" />
                <span>I consent to Tefla's using these details to manage this booking and send event and payment communications. I have permission to share the other delegates' contact details.</span>
              </label>
              <p className="mt-4 text-[12px] leading-relaxed text-[#5c6b5a]">
                Payment receipt does not guarantee an award or exhibition allocation. Please contact the organiser about package inclusions, cancellation and refund conditions before paying.
              </p>
              <button type="submit" disabled={busy || receipt} data-testid="booking-submit-btn" className="ofic-btn ofic-btn-lime mt-6 justify-center !py-4 text-base">
                <span data-testid="booking-submit-label">{busy ? "Please wait…" : paymentMethod === "online" ? `Proceed to payment · ${money(total, currency)}` : "Submit registration request"}</span>
                <span className="arrow">↗</span>
              </button>
              <div data-testid="booking-status" role="status" aria-live="polite" className="mt-4">
                {status.text && !receipt && (
                  <p className={`rounded-lg p-4 text-[13px] font-bold ${status.error ? "bg-red-50 text-red-900" : "bg-[#edf3e0] text-[#123d30]"}`}>{status.text}</p>
                )}
                {receipt && (
                  <div className="rounded-xl p-6" style={{ background: "var(--forest)", color: "var(--cream)" }} data-testid="booking-receipt">
                    <h3 className="font-display text-2xl text-[#d7ef8b]" data-testid="receipt-heading">
                      {receipt.paid ? "Payment verified. Thank you." : "Your registration request is saved."}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed opacity-90" data-testid="receipt-reference">Reference: {receipt.id}</p>
                    <p className="mt-2 text-sm leading-relaxed opacity-90">
                      {receipt.paid
                        ? "Your payment has been verified. Confirmation emails have been submitted for delivery."
                        : "No payment has been confirmed. Contact Tefla's with this reference to confirm availability, GST and bank transfer arrangements."}
                    </p>
                    <p className="mt-2 text-sm opacity-75">11 December 2026 · Ginger Hotel, Mumbai</p>
                    <div className="mt-5 flex flex-wrap gap-3 text-[13px] font-bold">
                      <a href={RATECARD} target="_blank" rel="noreferrer" className="text-[#d7ef8b] underline">Official rate card & bank details ↗</a>
                      <a href={`mailto:${EVENT.email}`} className="text-[#d7ef8b] underline">Contact the organiser ↗</a>
                    </div>
                  </div>
                )}
              </div>
              <p className="mt-5 text-[13px] text-[#5c6b5a]" data-testid="booking-support">
                Prefer to book with the team? <a href={`tel:${EVENT.phones[0].replace(/\s/g, "")}`} className="font-bold underline">{EVENT.phones[0]}</a> or <a href={`mailto:${EVENT.email}`} className="font-bold underline">{EVENT.email}</a>
              </p>
            </section>
          </form>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-2xl p-6" style={{ background: "var(--forest)", color: "var(--cream)" }} data-testid="booking-summary">
              <div className="flex items-center justify-between">
                <span className="eyebrow text-[10px] text-[#d7ef8b]">Your registration</span>
                <span className="badge-lime" data-testid="sidebar-early-badge">{early && pkg === "delegate" ? "EARLY BIRD" : "OFIC 2026"}</span>
              </div>
              <p className="mt-2 text-[12px] opacity-75">{early && pkg === "delegate" ? `Early bird through 10 November 2026 · Save ${money(currency === "INR" ? 2500 : 500, currency)} per delegate` : "11 December 2026 · Ginger Hotel, Mumbai"}</p>
              <div className="mt-5 border-y py-2" style={{ borderColor: "rgba(243,243,233,.2)" }} data-testid="summary-lines">
                {summaryLine(`${PACKAGE_LABELS[pkg]} × ${quantity}`, money(base, currency))}
                {currency === "INR" && summaryLine(config.gstPercent !== null ? `GST · ${config.gstPercent}%` : "GST", gstKnown ? money(tax, currency) : "To be confirmed")}
              </div>
              <div className="mt-4 flex items-baseline justify-between">
                <span className="eyebrow text-[10px] text-[#d7ef8b]">Total</span>
                <strong className="font-display text-3xl" data-testid="sidebar-total">{money(total, currency)}{gstKnown ? "" : " + GST"}</strong>
              </div>
              <p className="mt-2 text-[12px] opacity-75" data-testid="sidebar-tax">
                {gstKnown ? `Total for ${quantity} ${stall ? "stall" : `participant${quantity > 1 ? "s" : ""}`}.` : "GST will be confirmed by the organiser before payment."}
              </p>
              <div className="mt-6 border-t pt-5 text-[13px]" style={{ borderColor: "rgba(243,243,233,.2)" }}>
                <span className="eyebrow text-[10px] text-[#d7ef8b]">Your OFIC date</span>
                <p className="mt-2 font-extrabold">11 December, 2026</p>
                <p className="opacity-80">Ginger Hotel, Mumbai</p>
                <ul className="mt-3 grid gap-1 text-[12px] opacity-85">
                  <li>· Conference & industry conversations</li>
                  <li>· Exhibition & business networking</li>
                  <li>· Awards participation options</li>
                </ul>
              </div>
              <div className="mt-6 border-t pt-5 text-[12px]" style={{ borderColor: "rgba(243,243,233,.2)" }} data-testid="sidebar-contact">
                <img src="/assets/teflas-mark.png" alt="Organised by Tefla's" className="h-7 w-auto" />
                <div className="mt-3 grid gap-1.5 font-bold">
                  <a href={`mailto:${EVENT.email}`} className="text-[#d7ef8b] no-underline">{EVENT.email} ↗</a>
                  <a href={`tel:${EVENT.phones[0].replace(/\s/g, "")}`} className="text-[#d7ef8b] no-underline">{EVENT.phones[0]}</a>
                  <a href={RATECARD} target="_blank" rel="noreferrer" className="text-[#d7ef8b] no-underline">Official rate card ↗</a>
                </div>
              </div>
              <p className="mt-5 text-[11px] opacity-70">⌑ Secure online checkout via Razorpay</p>
            </div>
          </aside>
        </div>
      </main>
      <footer className="wrap flex items-center justify-between py-8 text-[12px] text-[#5c6b5a]">
        <span>© 2026 Tefla's · Organic Food India Conclave</span>
        <Link to="/" data-testid="register-footer-home" className="font-bold no-underline">Back to OFIC 2026 ↗</Link>
      </footer>
    </div>
  );
}
